import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './TeamMemberHierarchy.css';

// Utility function to mask names (except founders)
const maskName = (name) => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    // Single name: show first letter + ***
    return name.charAt(0) + '***';
  }
  // Show first letter of first name + *** + last name initial
  const firstNameInitial = parts[0].charAt(0);
  const lastNameInitial = parts[parts.length - 1].charAt(0);
  return `${firstNameInitial}***${lastNameInitial}`;
};

const TeamMemberHierarchy = () => {
  const { teamMemberId } = useParams();
  const navigate = useNavigate();

  const [teamMember, setTeamMember] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    if (teamMemberId) {
      fetchTeamMemberDetails();
    }
  }, [teamMemberId]);

  const fetchTeamMemberDetails = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/employees/member/${teamMemberId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();

      if (result.success && result.data) {
        const { tree, statistics: stats, member } = result.data;
        
        setStatistics(stats);

        if (tree && tree.length > 0) {
          const rootMember = tree[0];
          setTeamMember(rootMember);
          setTreeData(rootMember.children || []);
          
          // Auto-expand root node
          setExpandedNodes({ [rootMember.id]: true });
        } else if (member) {
          // Fallback if tree is empty but member exists
          setTeamMember(member);
          setTreeData([]);
          setExpandedNodes({});
        } else {
          setError('Team member not found');
        }
      } else {
        setError(result.message || 'Team member not found');
      }
    } catch (error) {
      console.error('Error fetching team member details:', error);
      setError('Failed to load team member details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (memberId, e) => {
    e.stopPropagation();
    setExpandedNodes(prev => ({
      ...prev,
      [memberId]: !prev[memberId]
    }));
  };

  const handleMemberClick = (memberId) => {
    navigate(`/team-members/view/${memberId}`);
  };

  const handleBackToHome = () => {
    navigate('/');
    // Scroll to the all members section after navigation
    setTimeout(() => {
      const section = document.getElementById('back-all-members');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleVacancyClick = (e) => {
    e.stopPropagation();
    navigate('/careers');
  };

  // Check if member is founder (handles both string and numeric position values)
  const isFounder = (member) => {
    return member.position === 'FOUNDER' || member.position === 0 || member.position === '0';
  };

  // Generate default avatar with initials
  const getDefaultAvatar = (name) => {
    const nameParts = name.trim().split(' ');
    let initials = '';
    
    if (nameParts.length >= 2) {
      initials = nameParts[0].charAt(0).toUpperCase() + nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    } else {
      initials = name.substring(0, 2).toUpperCase();
    }
    
    const colors = ['FFEAA7', 'FF6B6B', '4ECDC4', '95E1D3', 'F38181', 'AA96DA', 'FCBAD3', 'A8E6CF', 'FFD3B6', 'FFAAA5'];
    const nameHash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colorIndex = nameHash % colors.length;
    
    return `https://ui-avatars.com/api/?name=${initials}&background=${colors[colorIndex]}&color=fff&size=300&bold=true`;
  };

  // Get valid image URL
  const getImageUrl = (member) => {
    if (!member) return getDefaultAvatar('User');
    
    // Check is_display_img flag (handles 0, "0", false, null)
    const displayImg = member.is_display_img;
    if (displayImg === 0 || displayImg === '0' || displayImg === false) {
      return getDefaultAvatar(member.name);
    }
    
    // Check if image exists and is valid URL
    if (member.image && member.image.trim() !== '') {
      // Handle relative URLs by prepending API base if needed
      let imageUrl = member.image;
      if (imageUrl.startsWith('/storage/')) {
        imageUrl = `${process.env.REACT_APP_API_URL}${imageUrl}`;
      }
      return imageUrl;
    }
    
    return getDefaultAvatar(member.name);
  };

  const renderHierarchyNode = (member, level = 0) => {
    const hasChildren = member.children && member.children.length > 0;
    const isExpanded = expandedNodes[member.id];

    return (
      <div key={member.id} className="aurora-org-node" style={{ marginLeft: `${level * 30}px` }}>
        <div className="aurora-org-node-content">
          <div className="aurora-org-node-indicator">
            {hasChildren && (
              <button
                className="aurora-org-expand-toggle"
                onClick={(e) => toggleNode(member.id, e)}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
          </div>

          <div className="aurora-org-member-card" onClick={() => handleMemberClick(member.id)}>
            <div className="aurora-org-member-avatar">
              <img
                src={getImageUrl(member)}
                alt={member.name}
                onError={(e) => {
                  e.target.src = getDefaultAvatar(member.name);
                  e.target.onerror = null; // Prevent infinite loop
                }}
                loading="lazy"
              />
            </div>

            <div className="aurora-org-member-info">
              <h4>{isFounder(member) ? member.name : member.name}</h4>
              <p className="aurora-org-designation">{member.designation || 'Position not specified'}</p>
              <p className="aurora-org-department">
                {member.department_label || member.department || 'Department not specified'}
              </p>

              <div className="aurora-org-badges">
                {(member.children_count > 0 || (member.children && member.children.length > 0)) && (
                  <span className="aurora-org-child-count">
                    <i className="fas fa-users"></i> {member.children_count || member.children.length} Team {member.children_count === 1 ? 'Member' : 'Members'}
                  </span>
                )}
                
                {member.vacancy > 0 && (
                  <span 
                    className="aurora-org-vacancy-badge" 
                    onClick={handleVacancyClick}
                    title="Click to view career opportunities"
                  >
                    <i className="fas fa-briefcase"></i> {member.vacancy} {member.vacancy === 1 ? 'Vacancy' : 'Vacancies'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {isExpanded && hasChildren && (
          <div className="aurora-org-children-container">
            {member.children.map(child => renderHierarchyNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="aurora-org-loading">
        <div className="aurora-org-spinner"></div>
        <p>Loading team hierarchy...</p>
      </div>
    );
  }

  if (error || !teamMember) {
    return (
      <div className="aurora-org-error">
        <i className="fas fa-exclamation-triangle"></i>
        <h3>Error Loading Team Member</h3>
        <p>{error || 'Team member not found'}</p>
        <button
          onClick={() => navigate('/')}
          className="aurora-org-home-btn"
        >
          <i className="fas fa-home"></i> Go to Homepage
        </button>
      </div>
    );
  }

  return (
    <div className="aurora-org-container">
      <div className="aurora-org-header">
        <button
          onClick={handleBackToHome}
          className="aurora-org-back-btn"
        >
          <i className="fas fa-arrow-left"></i> Back to All Members
        </button>

        <div className="aurora-org-header-content">
          <div className="aurora-org-profile">
            <div className="aurora-org-avatar-wrapper">
              <img
                src={getImageUrl(teamMember)}
                alt={teamMember.name}
                className="aurora-org-profile-image"
                onError={(e) => {
                  e.target.src = getDefaultAvatar(teamMember.name);
                  e.target.onerror = null;
                }}
              />
            </div>
            <div className="aurora-org-profile-info">
              <h1>{isFounder(teamMember) ? teamMember.name : maskName(teamMember.name)}</h1>
              <p className="aurora-org-designation">{teamMember.designation || 'Position not specified'}</p>
              <p className="aurora-org-department">
                {teamMember.department_label || teamMember.department || 'Department not specified'}
              </p>

              {teamMember.email && (
                <p className="aurora-org-email">
                  <i className="fas fa-envelope"></i> {teamMember.email}
                </p>
              )}

              <div className="aurora-org-stats">
                <div className="aurora-org-stat-box">
                  <span className="aurora-org-stat-value">
                    {statistics?.direct_reports || teamMember.children_count || 0}
                  </span>
                  <span className="aurora-org-stat-label">Direct Reports</span>
                </div>
                {statistics?.total_team_members > 0 && (
                  <div className="aurora-org-stat-box">
                    <span className="aurora-org-stat-value">{statistics.total_team_members}</span>
                    <span className="aurora-org-stat-label">Total Team</span>
                  </div>
                )}
                {statistics?.team_depth > 0 && (
                  <div className="aurora-org-stat-box">
                    <span className="aurora-org-stat-value">{statistics.team_depth}</span>
                    <span className="aurora-org-stat-label">Team Depth</span>
                  </div>
                )}
                {teamMember.vacancy > 0 && (
                  <div 
                    className="aurora-org-stat-box aurora-org-stat-clickable" 
                    onClick={handleVacancyClick}
                  >
                    <span className="aurora-org-stat-value">{teamMember.vacancy}</span>
                    <span className="aurora-org-stat-label">
                      <i className="fas fa-briefcase"></i> {teamMember.vacancy === 1 ? 'Vacancy' : 'Vacancies'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="aurora-org-body">
        <h2>Team Hierarchy</h2>

        {treeData.length > 0 ? (
          <div className="aurora-org-tree">
            {treeData.map(member => renderHierarchyNode(member))}
          </div>
        ) : (
          <div className="aurora-org-empty">
            <i className="fas fa-user-friends"></i>
            <h3>No Team Members</h3>
            <p>This team member doesn't have any direct reports yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMemberHierarchy;